/**
 * Inbound-payment detail.
 *
 * Hero + tabs (Overview / Payer / Inbox). The Payer tab supports
 * step-up TOTP reveal (PR-6c) to unmask the MSISDN + full payer
 * dict; the Inbox tab cross-links to /callback-inbox/{id}/ where
 * the raw callback body can be revealed (PR-6b).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { DetailHero } from "@/components/payments/DetailHero";
import { ActionConfirmDialog } from "@/components/observability/ActionConfirmDialog";
import {
  asApiError,
  getInboundPayment,
  revealInboundPaymentPayer,
  type InboundPaymentDetail,
} from "@/lib/api";
import { INBOUND_STATE_BADGE_VARIANT } from "@/lib/state-badges";

export function InboundPaymentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["inbound-payment", id] as const,
    queryFn: () => getInboundPayment(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this inbound payment", {
        description: "It may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this inbound payment</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/inbound-payments" className="text-foreground underline">
                Back to list
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }
  return <Loaded ip={query.data} />;
}

function Loaded({ ip }: { ip: InboundPaymentDetail }) {
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [revealedPayer, setRevealedPayer] = useState<Record<string, unknown> | null>(
    null,
  );

  const revealMutation = useMutation({
    mutationFn: (vars: { reason: string; totpCode: string }) =>
      revealInboundPaymentPayer(ip.id, {
        totp_code: vars.totpCode,
        reason: vars.reason,
      }),
    onSuccess: (data) => {
      setRevealedPayer(data.payer);
      setRevealOpen(false);
      toast.success("Payer details revealed", {
        description: "An audit row has been written.",
      });
    },
    onError: (err: unknown) => {
      const body = asApiError(err);
      if (body?.error === "invalid_code") {
        setRevealError("That TOTP code didn't verify. Try the next one.");
      } else if (body?.error === "account_locked") {
        setRevealError("Account locked. Wait and try again.");
      } else if (body?.error === "totp_not_enrolled") {
        setRevealError("Your account doesn't have TOTP enrolled.");
      } else if (body?.error === "audit_unavailable") {
        setRevealError(
          "Audit log is unavailable right now — reveal aborted to preserve the forensic trail. Retry shortly.",
        );
      } else {
        setRevealError(body?.detail ?? "Couldn't reveal the payer details.");
      }
    },
  });

  const displayedPayer = revealedPayer ?? ip.payer;
  const isRevealed = revealedPayer !== null;

  return (
    <>
      <DetailHero
        backHref="/inbound-payments"
        backLabel="Back to inbound payments"
        id={ip.id}
        amount={ip.amount}
        currency={ip.currency}
        stateLabel={ip.state}
        stateVariant={INBOUND_STATE_BADGE_VARIANT[ip.state]}
        meta={
          <>
            <span className="font-medium text-foreground">{ip.tenant_name}</span>
            <Badge variant="muted" className="capitalize">
              {ip.tenant_environment}
            </Badge>
            <span>·</span>
            <span>{ip.plugin}</span>
            <span>·</span>
            <span title={ip.received_at}>
              received {new Date(ip.received_at).toLocaleString()}
            </span>
          </>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payer">Payer</TabsTrigger>
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Payment ID" mono>
                    {ip.id}
                  </Def>
                  <Def label="Plugin">{ip.plugin}</Def>
                  <Def label="Provider ref" mono>
                    {ip.provider_ref}
                  </Def>
                  <Def label="Account ref" mono>
                    {ip.account_ref || "—"}
                  </Def>
                  {ip.reverses_id ? (
                    <Def label="Reverses">
                      <Link
                        to={`/inbound-payments/${ip.reverses_id}`}
                        className="font-mono text-xs underline"
                      >
                        {ip.reverses_id}
                      </Link>
                    </Def>
                  ) : null}
                </Defs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tenant + timing</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Tenant">{ip.tenant_name}</Def>
                  <Def label="Environment">
                    <Badge variant="muted" className="capitalize">
                      {ip.tenant_environment}
                    </Badge>
                  </Def>
                  <Def label="Received">{new Date(ip.received_at).toLocaleString()}</Def>
                  <Def label="Created">{new Date(ip.created_at).toLocaleString()}</Def>
                  <Def label="Updated">{new Date(ip.updated_at).toLocaleString()}</Def>
                </Defs>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="payer">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="text-base">
                  Payer {isRevealed ? "(unmasked)" : "(masked)"}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {isRevealed
                    ? "Full payer details have been decrypted. An audit row recording who, when, and why has been written."
                    : "MSISDN is server-masked. Click Reveal to step-up via TOTP; the unmasked dict will be returned and audited."}
                </p>
              </div>
              {!isRevealed ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRevealError(null);
                    setRevealOpen(true);
                  }}
                >
                  <Eye />
                  Reveal
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {Object.keys(displayedPayer).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Payer data unavailable (legacy plaintext row or decryption error).
                </p>
              ) : (
                <Defs>
                  {Object.entries(displayedPayer).map(([k, v]) => (
                    <Def key={k} label={k} mono>
                      {typeof v === "string" ? v : JSON.stringify(v)}
                    </Def>
                  ))}
                </Defs>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inbox">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inbox message</CardTitle>
              <p className="text-xs text-muted-foreground">
                The provider-side inbox row this payment was projected from. Click
                through to the callback inbox detail page to reveal the raw
                callback bytes (step-up TOTP required).
              </p>
            </CardHeader>
            <CardContent>
              <Defs>
                <Def label="Inbox ID" mono>
                  <Link
                    to={`/callback-inbox/${ip.inbox_message_id}`}
                    className="underline"
                  >
                    {ip.inbox_message_id}
                  </Link>
                </Def>
                <Def label="Provider account" mono>
                  {ip.provider_account_id}
                </Def>
              </Defs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ActionConfirmDialog
        open={revealOpen}
        onOpenChange={(open) => {
          setRevealOpen(open);
          if (!open) setRevealError(null);
        }}
        title="Reveal payer details"
        description={
          "The MSISDN, full name, and any other captured fields will be decrypted and shown unmasked. " +
          "Step-up TOTP is required; the reveal will be logged in the super-admin audit log with the reason you provide."
        }
        confirmLabel="Reveal"
        confirmVariant="destructive"
        requireTotp
        pending={revealMutation.isPending}
        error={revealError}
        onSubmit={({ reason, totpCode }) =>
          revealMutation.mutate({ reason, totpCode })
        }
      />
    </>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd
        className={
          mono
            ? "min-w-0 break-all font-mono text-xs sm:text-right"
            : "min-w-0 text-sm sm:text-right"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default InboundPaymentDetailPage;
